import { useTranslation } from "react-i18next";

import type { GameRules, ShopItem } from "../../services/generated/client.js";
import { cn } from "../../lib/utils.js";
import { priceOf } from "./prices.js";

/**
 * Powers **you can buy right now**, on the screen where they work — ADR 0026.
 *
 * # The drawer is not replaced by a better drawer
 *
 * Six findings from the fourth party were one report: *"this is buried in
 * Más"*. Finding 3.11 asked for the drawer to become a modal of the enabled
 * actions, and the ADR rejected that: *"a challenge window is 30 seconds, and a
 * player who has to open a menu inside it has already lost it."* The other
 * reading of "enabled actions" is **placement**, and that is this — the powers
 * stop being a list you visit and become controls that are simply there at the
 * moment they work.
 *
 * # The engine already decides which those are
 *
 * `YouView::available_purchases` is the engine's own answer, phase-correct by
 * construction, so this component filters nothing by phase and mirrors no rule.
 * That matters more than it looks: **steal is not a turn power** — it is sold at
 * `TurnReady | Settled` to *anybody* with the credits, and three parties never
 * found it because the one moment it is purchasable is the one moment the phone
 * showed a junk drawer. Nothing here has to know that; the list already does.
 *
 * # ⚠️ Two items are deliberately not here
 *
 * | item | where it lives instead | why |
 * |---|---|---|
 * | `skip` | a floating control on the round screen | ADR 0026: *always true of your turn*, so it holds a fixed position rather than appearing among things that come and go |
 * | `challenge` | the challenge stage's timeline | you buy it **by aiming it** — a button that sends a purchase the engine refuses without a target is the bug phase 14 removed |
 *
 * `steal` is here but does not buy on tap for the same reason: it needs a
 * target, so it opens the picker.
 *
 * # No blurbs
 *
 * A name and a price. What a power *does* is teaching material, and teaching
 * material at the moment of action is a lost round — it belongs in the full
 * shop behind the cog, and in phase 21's walkthrough.
 */
export function Powers({
  rules,
  coins,
  availableNow,
  busy = false,
  onBuy,
  onAim,
  className,
}: {
  /** Prices, from `GameView.rules`. */
  rules: GameRules;
  /** Settled coins this side can spend. */
  coins: number;
  /** The engine's list — `YouView.available_purchases`, unfiltered. */
  availableNow: readonly ShopItem[];
  busy?: boolean;
  /** Buy one outright. */
  onBuy: (item: ShopItem) => void;
  /** Open the picker for one that needs a target. */
  onAim: (item: "steal") => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const offered = availableNow.filter((item) => item !== "skip" && item !== "challenge");

  if (offered.length === 0) {
    return null;
  }

  return (
    <section
      data-testid="powers"
      aria-label={t("shop.label")}
      className={cn("flex flex-wrap gap-2", className)}
    >
      {offered.map((item) => {
        const price = priceOf(rules, item);
        // ⚠️ Affordability is the **player's** limit, not the round's, and it is
        // the only one left to check here — the engine has already answered the
        // question about the round. It is still checked again server-side.
        const affordable = coins >= price;

        return (
          <button
            key={item}
            type="button"
            data-testid="power"
            data-item={item}
            disabled={busy || !affordable}
            onClick={() => {
              if (item === "steal") {
                onAim("steal");
                return;
              }
              onBuy(item);
            }}
            className={cn(
              "flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors",
              affordable
                ? "border-coin-400/40 bg-coin-400/10 hover:border-coin-400/70"
                : "cursor-not-allowed border-white/10 bg-stage-900/60 opacity-50",
            )}
          >
            <span>{t(`shop.items.${item}.name`)}</span>
            <span className="tabular-nums text-coin-400">{price}</span>
          </button>
        );
      })}
    </section>
  );
}
